import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { Modal, Progress, List, Button, Tag, Empty } from 'antd';
import {
  DownloadOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import {
  isExportProgressVisibleAtom,
  exportQueueAtom,
  hasActiveExportsAtom
} from '../../atoms/exportAtoms';
import { getExportService } from '../../services/exportService';
import './ExportProgressModal.scss';

export const ExportProgressModal: React.FC = () => {
  const [isVisible, setIsVisible] = useAtom(isExportProgressVisibleAtom);
  const [exportQueue] = useAtom(exportQueueAtom);
  const [hasActive] = useAtom(hasActiveExportsAtom);

  const exportService = getExportService();

  const handleDownload = (exportId: string) => {
    exportService.downloadExport(exportId);
  };

  const handleCancel = (exportId: string) => {
    exportService.cancelExport(exportId);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
      case 'cancelled':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'queued':
      case 'processing':
      case 'encoding':
        return <LoadingOutlined />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      case 'processing':
      case 'encoding': return 'processing';
      case 'queued': return 'default';
      default: return 'default';
    }
  };

  return (
    <Modal
      title="Export Progress"
      open={isVisible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>
      ]}
      className="export-progress-modal"
    >
      <div className="export-progress">
        {exportQueue.length === 0 ? (
          <Empty description="No exports in queue" />
        ) : (
          <List
            dataSource={exportQueue}
            renderItem={(item) => (
              <List.Item
                key={item.exportId}
                className="export-item"
                actions={[
                  item.status === 'completed' && (
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(item.exportId)}
                    >
                      Download
                    </Button>
                  ),
                  (item.status === 'queued' || item.status === 'processing') && (
                    <Button
                      type="link"
                      danger
                      onClick={() => handleCancel(item.exportId)}
                    >
                      Cancel
                    </Button>
                  )
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={getStatusIcon(item.status)}
                  title={
                    <div className="export-title">
                      <span className="platform-name">
                        {item.platform.charAt(0).toUpperCase() + item.platform.slice(1)}
                      </span>
                      <Tag color={getStatusColor(item.status)}>
                        {item.status}
                      </Tag>
                    </div>
                  }
                  description={
                    <div className="export-details">
                      <div className="export-step">{item.currentStep}</div>
                      {(item.status === 'processing' || item.status === 'encoding') && (
                        <Progress 
                          percent={item.progress} 
                          size="small"
                          status="active"
                        />
                      )}
                      {item.error && (
                        <div className="export-error">{item.error}</div>
                      )}
                      {item.result && (
                        <div className="export-result-info">
                          {(item.result.fileSize / 1024 / 1024).toFixed(2)} MB · {item.result.resolution.width}×{item.result.resolution.height}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
};
